import React from "react";
import { BaakasLogoLoader } from "../common/LoadingSkeleton";

export const ConversationSkeleton: React.FC = () => (
  <div className="animate-pulse">
    <div className="p-4 border-b border-gray-100">
      <div className="flex items-center space-x-4">
        <div className="w-14 h-14 bg-gray-200 rounded-full"></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
            <div className="h-3 bg-gray-200 rounded w-12"></div>
          </div>
          <div className="h-3 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    </div>
  </div>
);

export const ConversationListSkeleton: React.FC = () => (
  <div className="space-y-0">
    {[...Array(8)].map((_, index) => (
      <ConversationSkeleton key={index} />
    ))}
  </div>
);

export const MessageSkeleton: React.FC<{ isOwn?: boolean }> = ({
  isOwn = false,
}) => (
  <div
    className={`flex ${isOwn ? "justify-end" : "justify-start"} animate-pulse`}
  >
    <div
      className={`max-w-xs lg:max-w-md px-4 py-3 rounded-2xl space-y-2 ${
        isOwn ? "bg-gray-200 rounded-br-md" : "bg-gray-100 rounded-bl-md"
      }`}
    >
      <div className="h-4 bg-gray-300 rounded w-3/4"></div>
      <div className="h-4 bg-gray-300 rounded w-1/2"></div>
      <div className="h-3 bg-gray-300 rounded w-16 ml-auto"></div>
    </div>
  </div>
);

export const MessageListSkeleton: React.FC = () => (
  <div className="space-y-6 p-6">
    {[...Array(6)].map((_, index) => (
      <MessageSkeleton key={index} isOwn={index % 2 === 0} />
    ))}
  </div>
);

export const MessagingLoadingSkeleton: React.FC = () => (
  <div className="h-screen bg-gradient-to-br from-gray-50 to-white flex">
    {/* Sidebar Skeleton */}
    <div className="w-full md:w-1/3 lg:w-1/4 bg-white border-r border-gray-200 flex flex-col">
      {/* Header Skeleton */}
      <div className="p-6 border-b border-gray-200 animate-pulse">
        <div className="flex items-center justify-between mb-4">
          <div className="h-8 bg-gray-200 rounded w-32"></div>
          <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
        </div>
        <div className="h-12 bg-gray-200 rounded-xl"></div>
      </div>

      {/* Filter Tabs Skeleton */}
      <div className="p-4 border-b border-gray-200 animate-pulse">
        <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
          {[...Array(3)].map((_, index) => (
            <div
              key={index}
              className="flex-1 h-10 bg-gray-200 rounded-md"
            ></div>
          ))}
        </div>
      </div>

      {/* Conversations List Skeleton */}
      <div className="flex-1 overflow-hidden">
        <ConversationListSkeleton />
      </div>
    </div>

    {/* Chat Area Skeleton */}
    <div className="flex-1 flex flex-col">
      {/* Chat Header Skeleton */}
      <div className="bg-white border-b border-gray-200 p-6 animate-pulse">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
          <div className="flex-1">
            <div className="h-5 bg-gray-200 rounded w-1/3 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-24"></div>
          </div>
          <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
        </div>
      </div>

      {/* Messages Area Skeleton */}
      <div className="flex-1 overflow-hidden">
        <MessageListSkeleton />
      </div>

      {/* Input Area Skeleton */}
      <div className="bg-white border-t border-gray-200 p-6 animate-pulse">
        <div className="flex items-end space-x-4">
          <div className="w-10 h-10 bg-gray-200 rounded-xl"></div>
          <div className="w-10 h-10 bg-gray-200 rounded-xl"></div>
          <div className="flex-1 h-12 bg-gray-200 rounded-xl"></div>
          <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
        </div>
      </div>
    </div>
  </div>
);

export const InitializationSkeleton: React.FC = () => (
  <div className="h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center">
    <div className="text-center max-w-md mx-auto p-8">
      <div className="mb-8">
        <BaakasLogoLoader size="xl" className="mx-auto" aria-label="Initializing" />
      </div>
      <div className="h-8 bg-gray-200 rounded w-48 mx-auto mb-4 animate-pulse"></div>
      <div className="h-4 bg-gray-200 rounded w-64 mx-auto mb-2 animate-pulse"></div>
      <div className="h-4 bg-gray-200 rounded w-56 mx-auto animate-pulse"></div>

      {/* Loading dots */}
      <div className="flex justify-center items-center space-x-2 mt-8">
        <div className="w-2 h-2 bg-[#da2516] rounded-full animate-bounce"></div>
        <div
          className="w-2 h-2 bg-[#da2516] rounded-full animate-bounce"
          style={{ animationDelay: "0.1s" }}
        ></div>
        <div
          className="w-2 h-2 bg-[#da2516] rounded-full animate-bounce"
          style={{ animationDelay: "0.2s" }}
        ></div>
      </div>
    </div>
  </div>
);
